'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'

import { AppInstallAccountsDs } from '@src/components/blocks/app-install-accounts-ds'
import { WishlistShareSectionDS } from '@src/components/blocks/wishlist-share-section-ds'
import { SectionContentContainer } from '@src/ds/blocks/section-content-container'
import { TRACK_PAGE_TYPE } from '@src/quince-site-common/src/domains/PageTypes'
import { useAppInstallAccountsPresenter } from '@src/quince-site-common/src/organisms/appLayout/molecules/footer/molecules/app-install/app-install.presenter'

import type { WishlistHeadingDSProps } from './wishlist-heading-ds.types'

const headingWrapperCss = css({
  mt: 'space-64',
  textAlign: 'center',
  position: 'relative',
})

const headingCss = css({
  textStyle: 'title-24',
  textTransform: 'uppercase',
  mb: 'space-10',
})

const subHeadingCss = css({
  textStyle: 'body-16',
})

// Compact layout (app-install banner present): heading block on one side, banner on the other.
// Horizontal gutters come from the enclosing SectionContentContainer so the row aligns with the grid below.
const wishlistHeadingWrapperCss = css({
  display: 'flex',
  mt: 'space-32',
  flexDirection: { base: 'column', lg: 'row' },
  alignItems: { base: 'center', lg: 'flex-end' },
  justifyContent: { lg: 'space-between' },
  gap: { base: 'space-20', lg: '0' },
})

const headingWrapperCompactCss = css({
  display: 'flex',
  gap: 'space-4',
  flexDirection: { base: 'row', lg: 'column-reverse' },
  alignItems: { base: 'center', lg: 'stretch' },
  justifyContent: { base: 'space-between', lg: 'flex-start' },
  width: { base: 'full', lg: 'auto' },
})

// Decorative (ivy) title, weight 300: 24px/28 mobile, 32px/40 desktop — mirrors the legacy `.headingCompact`.
const headingCompactCss = css({
  textStyle: { base: 'title-24-dec', lg: 'title-32-dec' },
})

interface WishlistHeadingDSContentProps extends WishlistHeadingDSProps {
  showCompactLayout?: boolean
}

export function WishlistHeadingDS({ name, isSelfWishlist, wishListProductList }: WishlistHeadingDSProps) {
  const { t } = useTranslation()
  const { shouldRender: shouldRenderAppInstallAccounts } = useAppInstallAccountsPresenter({ placement: 'wishlist' })
  if (shouldRenderAppInstallAccounts) {
    return (
      <SectionContentContainer>
        <div className={wishlistHeadingWrapperCss}>
          <WishlistHeadingDSContent
            name={name}
            isSelfWishlist={isSelfWishlist}
            wishListProductList={wishListProductList}
            showCompactLayout
          />
          <AppInstallAccountsDs
            trackPageType={TRACK_PAGE_TYPE.WISHLIST}
            placement="wishlist"
            desktopConfig={{ title: t('get_the_app'), qrAlt: t('app_qr_alt') }}
            mobileConfig={{
              title: t('keep_track_fav_items'),
              actionLabel: t('get_the_app'),
            }}
          />
        </div>
      </SectionContentContainer>
    )
  }
  return (
    <WishlistHeadingDSContent name={name} isSelfWishlist={isSelfWishlist} wishListProductList={wishListProductList} />
  )
}

function WishlistHeadingDSContent({
  name,
  isSelfWishlist,
  wishListProductList,
  showCompactLayout,
}: WishlistHeadingDSContentProps) {
  const { t } = useTranslation('wishlist')
  return (
    <div className={showCompactLayout ? headingWrapperCompactCss : headingWrapperCss}>
      <h1 className={showCompactLayout ? headingCompactCss : headingCss}>
        {name ? t('favorites_possessive', { name: name }) : t('my_favorites')}
      </h1>
      {name ? <div className={subHeadingCss}>{t('heres_whats_on_their_wishlist')}</div> : null}
      {isSelfWishlist ? <WishlistShareSectionDS wishListProductList={wishListProductList} /> : null}
    </div>
  )
}
